import { startCase } from "lodash-es";
import type { QueryFunction } from "@tanstack/react-query";
import type { ListQueryResponse } from "@smartrent/ui";

import { instance } from "@/lib/hooks";
import { FeatureFlag } from "@/types";

import { TableFeatureFlag, Filters } from "./types";

/**
 * Parses a filter value that can be either a comma-separated string or an array of strings
 * @returns Array of string IDs
 */
function parseIdFilter(filter: string | string[] | undefined): string[] {
  if (!filter) {
    return [];
  }

  if (typeof filter === "string") {
    return filter.split(",").map((id) => id.trim());
  }

  if (Array.isArray(filter)) {
    return filter.map((id) => String(id));
  }

  return [];
}

/**
 * Transforms a single FeatureFlag from the API into the TableFeatureFlag format
 */
function transformFeatureFlag(flag: FeatureFlag): TableFeatureFlag {
  const globalGate = flag.gates.find((gate) => gate.for === "global");

  const orgGates = flag.gates.filter(
    (gate) =>
      typeof gate.for === "object" &&
      gate.for.entity === "organization" &&
      gate.enabled
  );

  const groupGates = flag.gates.filter(
    (gate) =>
      typeof gate.for === "object" &&
      gate.for.entity === "group" &&
      gate.enabled
  );

  const enabledOrgs = orgGates
    .map((gate) =>
      typeof gate.for === "object"
        ? { id: gate.for.entity_id, label: gate.for.label || "" }
        : null
    )
    .filter((item): item is { id: number; label: string } => item !== null);

  const enabledGroups = groupGates
    .map((gate) =>
      typeof gate.for === "object"
        ? { id: gate.for.entity_id, label: gate.for.label || "" }
        : null
    )
    .filter((item): item is { id: number; label: string } => item !== null);

  return {
    id: flag.name,
    flag,
    globallyEnabled: globalGate?.enabled || false,
    flagName: flag.name,
    displayName: startCase(flag.name),
    enabledOrganizations: enabledOrgs,
    enabledGroups: enabledGroups,
  };
}

/**
 * Applies all filters to the feature flag data
 */
function applyFilters(
  data: TableFeatureFlag[],
  filters: Filters
): TableFeatureFlag[] {
  let filtered = data;

  // Filter by display name
  if (filters.displayName) {
    const searchTerm = filters.displayName.toLowerCase();
    filtered = filtered.filter((item) =>
      item.displayName.toLowerCase().includes(searchTerm)
    );
  }

  // Filter by globally enabled status
  if (filters.globallyEnabled) {
    const isEnabled = filters.globallyEnabled === "true";
    filtered = filtered.filter((item) => item.globallyEnabled === isEnabled);
  }

  // Filter by organization
  if (filters.organization) {
    const orgIds = parseIdFilter(filters.organization);
    if (orgIds.length > 0) {
      filtered = filtered.filter((item) =>
        item.enabledOrganizations.some((org) => orgIds.includes(String(org.id)))
      );
    }
  }

  // Filter by group
  if (filters.group) {
    const groupIds = parseIdFilter(filters.group);
    if (groupIds.length > 0) {
      filtered = filtered.filter((item) =>
        item.enabledGroups.some((group) => groupIds.includes(String(group.id)))
      );
    }
  }

  return filtered;
}

/**
 * Sorts feature flag data by the specified column and direction
 * Handles the 4 sortable columns: displayName, globallyEnabled, enabledOrganizations, enabledGroups
 */
function applySorting(
  data: TableFeatureFlag[],
  sortColumn: string,
  sortDirection: "asc" | "desc"
): TableFeatureFlag[] {
  return [...data].sort((a, b) => {
    let comparison: number;

    // Sort by array length for org/group columns
    if (
      sortColumn === "enabledOrganizations" ||
      sortColumn === "enabledGroups"
    ) {
      comparison = a[sortColumn].length - b[sortColumn].length;
    }
    // Sort by boolean value for globallyEnabled
    else if (sortColumn === "globallyEnabled") {
      comparison = (a.globallyEnabled ? 1 : 0) - (b.globallyEnabled ? 1 : 0);
    }
    // Sort by string value for displayName
    else if (sortColumn === "displayName") {
      comparison = a.displayName
        .toLowerCase()
        .localeCompare(b.displayName.toLowerCase());
    }
    // Fallback for any other column (shouldn't happen with current config)
    else {
      comparison = 0;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });
}

export const fetchFeatureFlags: QueryFunction<
  ListQueryResponse<TableFeatureFlag>
> = async ({ queryKey }) => {
  const [, , filters] = queryKey as [string, Record<string, unknown>, Filters];

  // Fetch data from API
  const response = await instance.get<FeatureFlag[]>("/cmw-feature-flags");
  const flags: FeatureFlag[] = response.data;

  // Transform the data
  let transformedData = flags.map(transformFeatureFlag);

  // Apply filters
  transformedData = applyFilters(transformedData, filters);

  // Apply sorting
  if (filters.sort && filters.dir) {
    transformedData = applySorting(
      transformedData,
      filters.sort,
      filters.dir as "asc" | "desc"
    );
  }

  return {
    records: transformedData,
    current_page: 1,
    total_pages: 1,
    total_records: transformedData.length,
    per_page: transformedData.length,
  };
};
